import type { ScannedFile } from "./folderScan";

// 선택한 폴더의 파일을 브라우저가 실제로 열 수 있는지 확인한다.
//
// 읽기 비트가 있어도 파일 소유자가 브라우저 프로세스와 다르면 샌드박스가 open 을 거부한다
// (Docker 컨테이너가 root 로 만든 산출물이 대표적이다). 그대로 전송하면 XHR 은 network error 로만
// 실패하고, putPool 은 그 오류를 일시 오류로 분류한다. 그러면 사용자는 원인 없이 재시도만 보게 된다.
// 이 모듈은 선택 시점에 그런 파일을 찾아낸다.
//
// Owns:
// - 표본 추출 정책: 모든 파일 대신 균등 간격 표본만 읽는다. 소유권 문제는 폴더 단위로 균일하게
//   생긴다. 9만 개를 선택했을 때 모두 읽으면 선택 직후 화면이 멈춘 것처럼 보인다.
// - 읽기 거절의 원인 분류(permission/missing)와 표시용 예시 경로.
//
// Does not own:
// - 안내 문구·복구 명령 조립(page/upload/unreadableFolder), 전송 중 실패 분류(putPool).

export type FolderReadFailureReason = "permission" | "missing";

export type FolderReadFailure = {
  reason: FolderReadFailureReason;
  // 표시용 예시 경로(표본의 앞 몇 개). 사용자가 어느 파일인지 찾을 수 있게 한다.
  paths: readonly string[];
  // 표본이 모두 실패했는지. 폴더 전체가 같은 원인이라는 신호다(안내 문구의 강도를 정한다).
  everySampleFailed: boolean;
};

// 폴더 단위로 균일한 문제를 찾는 데 필요한 표본 크기. 선택 직후 지연이 느껴지지 않는 상한이기도 하다.
const SAMPLE_LIMIT = 24;
const DISPLAY_PATH_LIMIT = 3;

// 표본 파일을 1바이트씩 실제로 읽는다. 이상이 없으면 null.
export async function probeFolderReadability(
  files: readonly ScannedFile[],
  sampleLimit: number = SAMPLE_LIMIT,
): Promise<FolderReadFailure | null> {
  const sample = pickSample(files, sampleLimit);
  if (sample.length === 0) {
    return null;
  }
  const outcomes = await Promise.all(sample.map(probeOne));
  const failures = outcomes.filter((outcome) => outcome !== null);
  if (failures.length === 0) {
    return null;
  }
  // permission 이 하나라도 있으면 그 원인으로 안내한다. 조치가 있는 원인이 우선이다.
  const reason: FolderReadFailureReason = failures.some((entry) => entry.reason === "permission")
    ? "permission"
    : "missing";
  return {
    reason,
    paths: failures
      .filter((entry) => entry.reason === reason)
      .slice(0, DISPLAY_PATH_LIMIT)
      .map((entry) => entry.path),
    everySampleFailed: failures.length === sample.length,
  };
}

type ProbeFailure = { path: string; reason: FolderReadFailureReason };

async function probeOne(entry: ScannedFile): Promise<ProbeFailure | null> {
  try {
    await entry.file.slice(0, 1).arrayBuffer();
    return null;
  } catch (error) {
    return { path: entry.relativePath, reason: classifyReadError(error) };
  }
}

// 브라우저는 열 수 없는 파일을 NotReadableError 로, 선택 뒤 사라진 파일을 NotFoundError 로 거절한다.
function classifyReadError(error: unknown): FolderReadFailureReason {
  return error instanceof DOMException && error.name === "NotFoundError" ? "missing" : "permission";
}

// 0바이트 파일은 내용을 열지 않고도 성공할 수 있어 판정 근거가 될 수 없다. 표본에서 뺀다.
// 읽기 API 가 없는 환경에서는 표본을 비워 판정하지 않는다(진단 보조라 막지 않고 통과시킨다).
function pickSample(files: readonly ScannedFile[], limit: number): readonly ScannedFile[] {
  const candidates = files.filter(
    (entry) => entry.sizeBytes > 0 && typeof entry.file.arrayBuffer === "function",
  );
  if (candidates.length <= limit) {
    return candidates;
  }
  const stride = candidates.length / limit;
  return Array.from({ length: limit }, (_unused, index) => candidates[Math.floor(index * stride)]);
}
